/**
 * Anomaly Detection and Alert System
 * Monitors audit logs for suspicious activities and security threats
 */

import { collection, addDoc, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';

// Anomaly types and their configurations
const ANOMALY_TYPES = {
  RAPID_ACTIONS: {
    id: 'rapid_actions',
    name: 'Rapid Successive Actions',
    description: 'User performing many actions in a short time period',
    severity: 'medium',
    threshold: 10, // actions per minute
    timeWindow: 60000 // 1 minute in ms
  },
  FAILED_LOGINS: {
    id: 'failed_logins',
    name: 'Multiple Failed Login Attempts',
    description: 'Repeated login failures from same user or IP',
    severity: 'high',
    threshold: 5, // failed attempts
    timeWindow: 300000 // 5 minutes in ms
  },
  UNUSUAL_HOURS: {
    id: 'unusual_hours',
    name: 'Activity Outside Business Hours',
    description: 'User activity during unusual times',
    severity: 'low',
    businessHours: { start: 9, end: 17 }, // 9 AM to 5 PM
    timeZone: 'UTC'
  },
  BULK_OPERATIONS: {
    id: 'bulk_operations',
    name: 'Bulk Data Operations',
    description: 'Large number of records being modified',
    severity: 'medium',
    threshold: 50, // records affected
    timeWindow: 600000 // 10 minutes
  },
  PRIVILEGE_ESCALATION: {
    id: 'privilege_escalation',
    name: 'Privilege Escalation Attempt',
    description: 'User attempting to access unauthorized resources',
    severity: 'critical',
    unauthorizedActions: ['DELETE_USER', 'MODIFY_PERMISSIONS', 'ACCESS_ADMIN_PANEL']
  },
  GEOGRAPHIC_ANOMALY: {
    id: 'geographic_anomaly',
    name: 'Unusual Geographic Access',
    description: 'Access from unexpected geographic location',
    severity: 'medium',
    maxDistanceKm: 500 // Maximum distance from usual location
  },
  DATA_EXFILTRATION: {
    id: 'data_exfiltration',
    name: 'Potential Data Exfiltration',
    description: 'Large amounts of data being exported',
    severity: 'critical',
    threshold: 1000, // records exported
    timeWindow: 3600000 // 1 hour
  }
};

const SEVERITY_LEVELS = {
  LOW: 'low',
  MEDIUM: 'medium', 
  HIGH: 'high',
  CRITICAL: 'critical'
};

/**
 * Main anomaly detection service
 */
export class AnomalyDetector {
  constructor() {
    this.detectionRules = Object.values(ANOMALY_TYPES);
    this.alertThresholds = {
      [SEVERITY_LEVELS.LOW]: 5,
      [SEVERITY_LEVELS.MEDIUM]: 3,
      [SEVERITY_LEVELS.HIGH]: 2,
      [SEVERITY_LEVELS.CRITICAL]: 1
    };
  }

  /**
   * Analyze logs for anomalies
   * @param {Array} logs - Array of log entries to analyze
   * @param {Object} options - Analysis options
   * @returns {Array} Array of detected anomalies
   */
  async detectAnomalies(logs, options = {}) {
    const {
      timeWindow = 86400000, // 24 hours default
      enabledDetectors = Object.keys(ANOMALY_TYPES),
      userContext = null
    } = options;

    const anomalies = [];
    
    // Filter logs to time window
    const now = new Date();
    const cutoffTime = new Date(now.getTime() - timeWindow);
    const recentLogs = logs.filter(log => 
      new Date(log.timestamp) >= cutoffTime
    );

    // Run each detection rule
    for (const detectorId of enabledDetectors) {
      const detector = ANOMALY_TYPES[detectorId];
      if (detector) {
        try {
          const detectedAnomalies = await this.runDetector(detector, recentLogs, userContext);
          anomalies.push(...detectedAnomalies);
        } catch (error) {
          console.error(`Error running detector ${detectorId}:`, error);
        }
      }
    }

    // Sort by severity and timestamp
    anomalies.sort((a, b) => {
      const severityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
      if (severityOrder[a.severity] !== severityOrder[b.severity]) {
        return severityOrder[b.severity] - severityOrder[a.severity];
      }
      return new Date(b.timestamp) - new Date(a.timestamp);
    });

    // Save critical anomalies to database
    await this.saveAnomalies(anomalies.filter(a => 
      a.severity === SEVERITY_LEVELS.CRITICAL || a.severity === SEVERITY_LEVELS.HIGH
    ));

    return anomalies;
  }

  /**
   * Run a specific detector
   */
  async runDetector(detector, logs, userContext) {
    switch (detector.id) {
      case 'rapid_actions':
        return this.detectRapidActions(logs, detector);
      case 'failed_logins':
        return this.detectFailedLogins(logs, detector);
      case 'unusual_hours':
        return this.detectUnusualHours(logs, detector);
      case 'bulk_operations':
        return this.detectBulkOperations(logs, detector);
      case 'privilege_escalation':
        return this.detectPrivilegeEscalation(logs, detector);
      case 'data_exfiltration':
        return this.detectDataExfiltration(logs, detector);
      default:
        return [];
    }
  }

  /**
   * Detect rapid successive actions
   */
  detectRapidActions(logs, config) {
    const anomalies = [];
    const userActions = {};

    logs.forEach(log => {
      const userId = log.userId;
      if (!userActions[userId]) {
        userActions[userId] = [];
      }
      userActions[userId].push(log);
    });

    Object.entries(userActions).forEach(([userId, userLogs]) => {
      const sortedLogs = userLogs.sort((a, b) => 
        new Date(a.timestamp) - new Date(b.timestamp)
      );

      for (let i = 0; i < sortedLogs.length; i++) {
        const windowStart = new Date(sortedLogs[i].timestamp);
        const windowEnd = new Date(windowStart.getTime() + config.timeWindow);
        
        const actionsInWindow = sortedLogs.filter(log => {
          const logTime = new Date(log.timestamp);
          return logTime >= windowStart && logTime <= windowEnd;
        });

        if (actionsInWindow.length >= config.threshold) {
          anomalies.push({
            id: `rapid_actions_${userId}_${windowStart.getTime()}`,
            type: config.id,
            severity: config.severity,
            timestamp: windowStart.toISOString(),
            userId: userId,
            description: `User performed ${actionsInWindow.length} actions in ${config.timeWindow / 1000} seconds`,
            details: {
              actionCount: actionsInWindow.length,
              timeWindow: config.timeWindow,
              actions: actionsInWindow.map(log => ({
                action: log.action,
                timestamp: log.timestamp
              }))
            },
            riskScore: Math.min(100, (actionsInWindow.length / config.threshold) * 50)
          });
          
          // Skip ahead to avoid duplicate detections
          i += actionsInWindow.length - 1;
        }
      }
    });

    return anomalies;
  }

  /**
   * Detect failed login attempts
   */
  detectFailedLogins(logs, config) {
    const anomalies = [];
    const failedAttempts = {};

    // Group failed login attempts by user/IP
    logs.filter(log => 
      log.action === 'LOGIN_FAILED' || log.outcome === 'failure'
    ).forEach(log => {
      const key = log.userEmail || log.ipAddress || log.userId;
      if (!failedAttempts[key]) {
        failedAttempts[key] = [];
      }
      failedAttempts[key].push(log);
    });

    Object.entries(failedAttempts).forEach(([identifier, attempts]) => {
      if (attempts.length >= config.threshold) {
        const timeSpan = new Date(attempts[attempts.length - 1].timestamp) - 
                        new Date(attempts[0].timestamp);
        
        if (timeSpan <= config.timeWindow) {
          anomalies.push({
            id: `failed_logins_${identifier}_${Date.now()}`,
            type: config.id,
            severity: config.severity,
            timestamp: attempts[0].timestamp,
            userId: attempts[0].userId,
            description: `${attempts.length} failed login attempts in ${Math.round(timeSpan / 1000)} seconds`,
            details: {
              attemptCount: attempts.length,
              timeSpan: timeSpan,
              identifier: identifier,
              attempts: attempts.map(log => ({
                timestamp: log.timestamp,
                ipAddress: log.ipAddress,
                userAgent: log.userAgent
              }))
            },
            riskScore: Math.min(100, (attempts.length / config.threshold) * 70)
          });
        }
      }
    });

    return anomalies;
  }

  /**
   * Detect unusual hours activity
   */
  detectUnusualHours(logs, config) {
    const anomalies = [];
    const { businessHours } = config;

    const afterHoursLogs = logs.filter(log => {
      const logTime = new Date(log.timestamp);
      const hour = logTime.getUTCHours();
      return hour < businessHours.start || hour >= businessHours.end;
    });

    if (afterHoursLogs.length > 0) {
      // Group by user
      const userActivity = {};
      afterHoursLogs.forEach(log => {
        const userId = log.userId;
        if (!userActivity[userId]) {
          userActivity[userId] = [];
        }
        userActivity[userId].push(log);
      });

      Object.entries(userActivity).forEach(([userId, userLogs]) => {
        if (userLogs.length >= 3) { // Minimum threshold for unusual hours
          anomalies.push({
            id: `unusual_hours_${userId}_${Date.now()}`,
            type: config.id,
            severity: config.severity,
            timestamp: userLogs[0].timestamp,
            userId: userId,
            description: `User active outside business hours (${userLogs.length} actions)`,
            details: {
              actionCount: userLogs.length,
              businessHours: businessHours,
              actions: userLogs.map(log => ({
                action: log.action,
                timestamp: log.timestamp,
                hour: new Date(log.timestamp).getUTCHours()
              }))
            },
            riskScore: Math.min(100, userLogs.length * 10)
          });
        }
      });
    }

    return anomalies;
  }

  /**
   * Detect bulk operations
   */
  detectBulkOperations(logs, config) {
    const anomalies = [];
    const bulkActions = ['BULK_DELETE', 'BULK_UPDATE', 'BULK_EXPORT', 'MASS_DELETE'];

    logs.filter(log => 
      bulkActions.includes(log.action) || 
      (log.metadata && log.metadata.recordCount >= config.threshold)
    ).forEach(log => {
      const recordCount = log.metadata?.recordCount || config.threshold;
      
      if (recordCount >= config.threshold) {
        anomalies.push({
          id: `bulk_operations_${log.id}`,
          type: config.id,
          severity: config.severity,
          timestamp: log.timestamp,
          userId: log.userId,
          description: `Bulk operation affecting ${recordCount} records`,
          details: {
            action: log.action,
            recordCount: recordCount,
            entity: log.entity,
            metadata: log.metadata
          },
          riskScore: Math.min(100, (recordCount / config.threshold) * 60)
        });
      }
    });

    return anomalies;
  }

  /**
   * Detect privilege escalation attempts
   */
  detectPrivilegeEscalation(logs, config) {
    const anomalies = [];

    logs.filter(log => 
      config.unauthorizedActions.includes(log.action) ||
      (log.outcome === 'failure' && log.error?.includes('permission'))
    ).forEach(log => {
      anomalies.push({
        id: `privilege_escalation_${log.id}`,
        type: config.id,
        severity: config.severity,
        timestamp: log.timestamp,
        userId: log.userId,
        description: `Potential privilege escalation attempt: ${log.action}`,
        details: {
          action: log.action,
          outcome: log.outcome,
          error: log.error,
          entity: log.entity,
          ipAddress: log.ipAddress
        },
        riskScore: 90
      });
    });

    return anomalies;
  }

  /**
   * Detect potential data exfiltration
   */
  detectDataExfiltration(logs, config) {
    const anomalies = [];
    const exportActions = ['EXPORT', 'DOWNLOAD', 'BULK_EXPORT'];

    const exports = logs.filter(log => 
      exportActions.some(action => log.action.includes(action))
    );

    // Group exports by user and time window
    const userExports = {};
    exports.forEach(log => {
      const userId = log.userId;
      if (!userExports[userId]) {
        userExports[userId] = [];
      }
      userExports[userId].push(log);
    });

    Object.entries(userExports).forEach(([userId, exportLogs]) => {
      const sortedExports = exportLogs.sort((a, b) => 
        new Date(a.timestamp) - new Date(b.timestamp)
      );

      for (let i = 0; i < sortedExports.length; i++) {
        const windowStart = new Date(sortedExports[i].timestamp);
        const windowEnd = new Date(windowStart.getTime() + config.timeWindow);
        
        const exportsInWindow = sortedExports.filter(log => {
          const logTime = new Date(log.timestamp);
          return logTime >= windowStart && logTime <= windowEnd;
        });

        const totalRecords = exportsInWindow.reduce((sum, log) => 
          sum + (log.metadata?.recordCount || 0), 0
        );

        if (totalRecords >= config.threshold) {
          anomalies.push({
            id: `data_exfiltration_${userId}_${windowStart.getTime()}`,
            type: config.id,
            severity: config.severity,
            timestamp: windowStart.toISOString(),
            userId: userId,
            description: `Large data export: ${totalRecords} records in ${config.timeWindow / 60000} minutes`,
            details: {
              recordCount: totalRecords,
              exportCount: exportsInWindow.length,
              timeWindow: config.timeWindow,
              exports: exportsInWindow.map(log => ({
                action: log.action,
                timestamp: log.timestamp,
                recordCount: log.metadata?.recordCount
              }))
            },
            riskScore: Math.min(100, (totalRecords / config.threshold) * 80)
          });
        }
      }
    });

    return anomalies;
  }

  /**
   * Save anomalies to database
   */
  async saveAnomalies(anomalies) {
    try {
      for (const anomaly of anomalies) {
        await addDoc(collection(db, 'security_alerts'), {
          ...anomaly,
          detectedAt: new Date().toISOString(),
          status: 'new',
          investigated: false
        });
      }
    } catch (error) {
      console.error('Error saving anomalies to database:', error);
    }
  }

  /**
   * Get security alert summary
   */
  async getAlertSummary(timeWindow = 86400000) {
    try {
      const cutoffTime = new Date(Date.now() - timeWindow);
      const q = query(
        collection(db, 'security_alerts'),
        where('detectedAt', '>=', cutoffTime.toISOString()),
        orderBy('detectedAt', 'desc'),
        limit(100)
      );

      const snapshot = await getDocs(q);
      const alerts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const summary = {
        total: alerts.length,
        bySeverity: {
          critical: alerts.filter(a => a.severity === 'critical').length,
          high: alerts.filter(a => a.severity === 'high').length,
          medium: alerts.filter(a => a.severity === 'medium').length,
          low: alerts.filter(a => a.severity === 'low').length
        },
        byType: {},
        recentAlerts: alerts.slice(0, 10),
        riskScore: this.calculateOverallRiskScore(alerts)
      };

      // Count by type
      alerts.forEach(alert => {
        summary.byType[alert.type] = (summary.byType[alert.type] || 0) + 1;
      });

      return summary;
    } catch (error) {
      console.error('Error getting alert summary:', error);
      return null;
    }
  }

  /**
   * Calculate overall risk score
   */
  calculateOverallRiskScore(alerts) {
    if (alerts.length === 0) return 0;

    const weights = {
      critical: 10,
      high: 7,
      medium: 4,
      low: 1
    };

    const totalWeight = alerts.reduce((sum, alert) => 
      sum + (weights[alert.severity] || 0), 0
    );

    return Math.min(100, Math.round(totalWeight / alerts.length * 10));
  }
}

// Export singleton instance
export const anomalyDetector = new AnomalyDetector();

// Export types and configurations
export { ANOMALY_TYPES, SEVERITY_LEVELS };
